import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import useClassSheetDelivery from "../hooks/useClassSheetDelivery";
import useVenueDateTime from "../hooks/useVenueDateTime";

// Card #2844 -- per-class score-sheet email delivery, so show staff can see
// which riders were emailed their scored test, which failed (with the error),
// which are held until the class is placed, and which have no address, without
// reading logs. Read-only (see useClassSheetDelivery). "Sent" means the mail
// service accepted the message, not that it reached an inbox.

// Problems first, so a failure is never buried under a screen of "Sent".
const STATE_ORDER = ["FAILED", "HELD", "NO_EMAIL", "NOT_SENT", "NOT_REQUESTED", "SENT"];

const STATE_DISPLAY = {
  SENT: { color: "success", variant: "filled", labelKey: "ring.sheetDelivery.state.SENT" },
  FAILED: { color: "error", variant: "filled", labelKey: "ring.sheetDelivery.state.FAILED" },
  HELD: { color: "warning", variant: "outlined", labelKey: "ring.sheetDelivery.state.HELD" },
  NO_EMAIL: { color: "warning", variant: "filled", labelKey: "ring.sheetDelivery.state.NO_EMAIL" },
  NOT_SENT: { color: "default", variant: "outlined", labelKey: "ring.sheetDelivery.state.NOT_SENT" },
  NOT_REQUESTED: { color: "default", variant: "outlined", labelKey: "ring.sheetDelivery.state.NOT_REQUESTED" },
};

export default function ClassSheetDeliveryPanel({ classId, open }) {
  const formatVenueLocalDateTime = useVenueDateTime();
  const { t } = useTranslation("scoreadmin");
  const { fetchDelivery } = useClassSheetDelivery();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!classId) return;
    setLoading(true);
    setError(false);
    try {
      setData(await fetchDelivery(classId));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [classId, fetchDelivery]);

  useEffect(() => {
    if (!open || !classId) return undefined;
    setData(null);
    load();
    return undefined;
  }, [open, classId, load]);

  const riders = useMemo(() => {
    const rows = [...(data?.riders || [])];
    rows.sort((a, b) => {
      const sa = STATE_ORDER.indexOf(a.delivery);
      const sb = STATE_ORDER.indexOf(b.delivery);
      if (sa !== sb) return sa - sb;
      return String(a.competitorNumber ?? "").localeCompare(String(b.competitorNumber ?? ""), undefined, {
        numeric: true,
      });
    });
    return rows;
  }, [data]);

  const counts = data?.counts || {};

  return (
    <Box data-testid="class-sheet-delivery" sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6" color="text.primary">
          {t("ring.sheetDelivery.heading")}
        </Typography>
        <Button
          size="small"
          onClick={load}
          disabled={loading}
          data-testid="class-sheet-delivery-refresh"
        >
          {t("ring.sheetDelivery.refresh")}
        </Button>
      </Stack>

      {loading && !data && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} data-testid="class-sheet-delivery-error">
          {t("ring.sheetDelivery.loadError")}
        </Alert>
      )}

      {data && (
        <>
          {!data.classPlaced && (
            <Alert severity="info" sx={{ mb: 1 }} data-testid="class-sheet-delivery-not-placed">
              {t("ring.sheetDelivery.notPlaced")}
            </Alert>
          )}
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }} data-testid="class-sheet-delivery-counts">
            {STATE_ORDER.filter((s) => counts[s] > 0).map((s) => (
              <Chip
                key={s}
                size="small"
                color={STATE_DISPLAY[s].color}
                variant={STATE_DISPLAY[s].variant}
                label={t("ring.sheetDelivery.count", {
                  state: t(STATE_DISPLAY[s].labelKey),
                  count: counts[s],
                })}
                data-testid={`class-sheet-delivery-count-${s}`}
              />
            ))}
          </Stack>
          {riders.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("ring.sheetDelivery.noRiders")}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("ring.sheetDelivery.colRider")}</TableCell>
                  <TableCell>{t("ring.sheetDelivery.colState")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {riders.map((r) => {
                  const display = STATE_DISPLAY[r.delivery] || STATE_DISPLAY.NOT_SENT;
                  const sentAt = r.sheetEmailedAt ? formatVenueLocalDateTime(r.sheetEmailedAt) : null;
                  return (
                    <TableRow key={r.riderId} data-testid={`class-sheet-delivery-row-${r.riderId}`}>
                      <TableCell sx={{ color: "text.primary" }}>
                        {r.competitorNumber != null ? `#${r.competitorNumber}, ` : ""}
                        {r.riderName}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ wordBreak: "break-all" }}
                        >
                          {r.email || t("ring.sheetDelivery.noAddress")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={display.color}
                          variant={display.variant}
                          label={t(display.labelKey)}
                          data-testid={`class-sheet-delivery-state-${r.riderId}`}
                          data-delivery-state={r.delivery}
                        />
                        {r.delivery === "FAILED" && r.emailError && (
                          <Typography
                            variant="caption"
                            color="error.main"
                            display="block"
                            data-testid={`class-sheet-delivery-error-${r.riderId}`}
                          >
                            {r.emailError}
                          </Typography>
                        )}
                        {r.delivery === "SENT" && sentAt && (
                          <Tooltip title={t("ring.sheetDelivery.sentAtTooltip", { when: sentAt })}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {sentAt}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </Box>
  );
}
